import { MakeLogicType, actions, connect, kea, path, reducers, selectors } from 'kea'

import { dayjs } from 'lib/dayjs'
import { organizationLogic } from 'scenes/organizationLogic'
import { teamLogic } from 'scenes/teamLogic'

import { TeamType } from '~/types'

import type { OrganizationType, TeamPublicType } from '../../../types'

const SNOOZE_DAYS = 30
// Don't nag accounts that can't yet have at-risk data: only warn once the account is within 90 days of its
// retention horizon (or past it).
const WARN_WITHIN_DAYS = 90

// Humanize a retention window in months into the attributive form the banner uses ("your 7-year data retention"),
// which stays singular and hyphenated because it modifies a noun: whole years read as "N-year", anything else
// as "N-month".
function retentionMonthsLabel(months: number): string {
    if (months % 12 === 0) {
        return `${months / 12}-year`
    }
    return `${months}-month`
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataRetentionBannerLogicValues {
    currentOrganization: OrganizationType | null // organizationLogic
    currentTeam: TeamPublicType | TeamType | null // teamLogic
    accountAgeEligible: boolean
    isSnoozed: boolean
    retentionApplies: boolean
    retentionEnforced: boolean
    retentionMonths: number | null
    retentionPeriodLabel: string | null
    snoozedUntil: string | null
    warningEligible: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataRetentionBannerLogicActions {
    snooze: () => {
        value: true
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataRetentionBannerLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        retentionEnforced: (currentTeam: TeamPublicType | TeamType | null) => boolean
        retentionMonths: (currentTeam: TeamPublicType | TeamType | null) => number | null
        retentionPeriodLabel: (retentionMonths: number | null) => string | null
        isSnoozed: (snoozedUntil: string | null) => boolean
        accountAgeEligible: (currentOrganization: OrganizationType | null, retentionMonths: number | null) => boolean
        retentionApplies: (retentionEnforced: boolean, accountAgeEligible: boolean) => boolean
        warningEligible: (retentionApplies: boolean, isSnoozed: boolean) => boolean
    }
}

export type dataRetentionBannerLogicType = MakeLogicType<
    dataRetentionBannerLogicValues,
    dataRetentionBannerLogicActions,
    Record<string, any>,
    dataRetentionBannerLogicMeta
>

// Global (per-browser) snooze + plan-derived eligibility for the events data-retention warning. The per-insight
// "does this query's range exceed retention" check lives in insightRetentionBannerLogic; this one stays a singleton
// so the snooze is shared across every insight rather than dismissed once per insight.
export const dataRetentionBannerLogic = kea<dataRetentionBannerLogicType>([
    path(['scenes', 'insights', 'dataRetention', 'dataRetentionBannerLogic']),
    connect({
        values: [teamLogic, ['currentTeam'], organizationLogic, ['currentOrganization']],
    }),
    actions({
        snooze: true,
    }),
    reducers({
        snoozedUntil: [
            null as string | null,
            { persist: true },
            {
                snooze: () => dayjs().add(SNOOZE_DAYS, 'day').toISOString(),
            },
        ],
    }),
    selectors({
        retentionEnforced: [
            (s) => [s.currentTeam],
            (currentTeam: TeamType | null | import('~/types').TeamPublicType): boolean =>
                !!(currentTeam as TeamType | null)?.events_retention_enforced,
        ],
        retentionMonths: [
            (s) => [s.currentTeam],
            (currentTeam: TeamType | null | import('~/types').TeamPublicType): number | null =>
                (currentTeam as TeamType | null)?.event_retention_months ?? null,
        ],
        retentionPeriodLabel: [
            (s) => [s.retentionMonths],
            (retentionMonths: number | null): string | null =>
                retentionMonths ? retentionMonthsLabel(retentionMonths) : null,
        ],
        isSnoozed: [
            (s) => [s.snoozedUntil],
            (snoozedUntil: string | null): boolean => !!snoozedUntil && dayjs(snoozedUntil).isAfter(dayjs()),
        ],
        accountAgeEligible: [
            (s) => [s.currentOrganization, s.retentionMonths],
            (
                currentOrganization: null | import('~/types').OrganizationType,
                retentionMonths: number | null
            ): boolean => {
                if (!currentOrganization?.created_at || !retentionMonths) {
                    return false
                }
                // Eligible once the account is old enough to have data within 90 days of (or past) its retention
                // horizon — i.e. created before now − retention + 90 days.
                const horizon = dayjs().subtract(retentionMonths, 'month').add(WARN_WITHIN_DAYS, 'day')
                return dayjs(currentOrganization.created_at).isBefore(horizon)
            },
        ],
        retentionApplies: [
            (s) => [s.retentionEnforced, s.accountAgeEligible],
            (retentionEnforced: boolean, accountAgeEligible: boolean): boolean =>
                retentionEnforced && accountAgeEligible,
        ],
        warningEligible: [
            (s) => [s.retentionApplies, s.isSnoozed],
            (retentionApplies: boolean, isSnoozed: boolean): boolean => retentionApplies && !isSnoozed,
        ],
    }),
])
